import { usePermissions } from '@ltvco/refresh-lib/ctx';
import { Stack } from '@ltvco/refresh-lib/theme';

import { FreePlanDisclaimer } from 'components/ChatAI/components/FreePlanDisclaimer';

import { RetrySearchInstruction } from '../../components/RetrySearchInstruction';
import { EnterRepairDataInstruction } from '../EnterRepairDataInstruction/EnterRepairDataInstruction';
import { RepairCostEstimatorResultCard } from '../RepairCostEstimatorResultCard/RepairCostEstimatorResultCard';

import type { RepairCostEstimate } from '@ltvco/refresh-lib/api';

type RepairCostEstimatorContentProps = {
  onSendMessage?: (message?: string, imageBase64?: string) => void;
  firstEstimateData: RepairCostEstimate | null | undefined;
  shouldResetInputs: boolean;
  onResetComplete: () => void;
  onProcessingStateChange?: (isProcessing: boolean) => void;
  hasError?: boolean;
  onNewEstimate: () => void;
  isLoading: boolean;
};

/**
 * RepairCostEstimatorContent - Main content container for Repair Cost Estimator
 * Shows instruction prompt and first estimate result if available
 * @component
 */
export const RepairCostEstimatorContent = ({
  onSendMessage,
  firstEstimateData,
  shouldResetInputs,
  onResetComplete,
  onProcessingStateChange,
  hasError = false,
  onNewEstimate,
  isLoading,
}: RepairCostEstimatorContentProps) => {
  const { isFreePlan } = usePermissions();
  const hasStarted = Boolean(firstEstimateData) || hasError;
  const showResultCard = firstEstimateData && !hasError;

  return (
    <Stack alignItems="center" gap={1} width="100%">
      <EnterRepairDataInstruction
        onSendMessage={onSendMessage}
        hasConversationStarted={hasStarted}
        shouldResetInputs={shouldResetInputs}
        onResetComplete={onResetComplete}
        onProcessingStateChange={onProcessingStateChange}
        isLoading={isLoading}
      />
      {showResultCard && (
        <>
          <RepairCostEstimatorResultCard estimateData={firstEstimateData} />
          {isFreePlan && <FreePlanDisclaimer entityType="repair_estimate" />}
          <RetrySearchInstruction onRetrySearch={onNewEstimate} />
        </>
      )}
    </Stack>
  );
};
